import { Link } from "react-router-dom";

import { IconGpus } from "../components/ui/icons";

import { useCapacity, useMyUsage, useMyWorkspaces } from "../api/queries";
import { useSession } from "../auth/SessionProvider";
import { ClusterProblem, isClusterProblem } from "../components/ClusterProblem";
import { CapacitySection } from "../components/capacity/CapacitySection";
import { StartMachineCard } from "../components/capacity/StartMachineCard";
import { WaitingSection } from "../components/capacity/WaitingSection";
import { PageHeader } from "../components/layout/PageHeader";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import { WorkspaceCard } from "../components/workspace/WorkspaceCard";
import { familyLabel, needsNewMachine, poolsBySize } from "../lib/capacity";
import { formatGpuHours, pluralize } from "../lib/format";
import type { Capacity } from "../api/types";

/** One line naming the machine shapes the console can start on demand. */
function fleetSubtitle(capacity: Capacity | undefined): string {
  if (!capacity) {
    return "Reading the cluster…";
  }
  const pools = poolsBySize(capacity);
  if (pools.length > 0) {
    const shapes = pools
      .map(
        (pool) =>
          `${pool.gpus_per_node}-GPU${
            pool.family ? ` ${familyLabel(pool.family)}` : ""
          } machines`,
      )
      .join(" and ");
    return `${shapes}, started on demand and released when idle`;
  }
  // The cluster answered; only Scaleway did not. Saying "reading…" here would
  // claim the screen is unfinished when it is simply complete without pools.
  return `${pluralize(capacity.gpu_total, "GPU")} on ${pluralize(
    capacity.nodes.length,
    "machine",
  )}`;
}

/**
 * Home: one button to get GPUs, every workspace you hold, and the real fleet.
 *
 * The Get GPUs action never disappears: a researcher may hold several
 * workspaces at once (`docs/API.md`), and hiding the primary action behind a
 * running one is exactly the rationing this product does not do.
 *
 * Returns:
 *     The home screen.
 */
export function HomePage(): JSX.Element {
  const capacity = useCapacity();
  const workspaces = useMyWorkspaces();
  const usage = useMyUsage(7);
  const { me } = useSession();

  const blocking = isClusterProblem(capacity.error)
    ? capacity.error
    : isClusterProblem(workspaces.error)
      ? workspaces.error
      : null;

  if (blocking) {
    return (
      <>
        <PageHeader eyebrow="Fleet" title="GPUs" subtitle="No cluster to read" />
        <ClusterProblem
          error={blocking}
          onRetry={() => {
            void capacity.refetch();
            void workspaces.refetch();
          }}
        />
      </>
    );
  }

  const mine = workspaces.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Fleet"
        title="GPUs"
        subtitle={fleetSubtitle(capacity.data)}
      />

      <section className="hero" aria-labelledby="hero-title">
        <div className="hero__body">
          <h2 className="hero__title" id="hero-title">
            Get GPUs
          </h2>
          <p className="hero__text">
            Ask for the GPUs you need and get a Linux machine you can SSH
            into, with your files already there. Done for the day? Give the
            GPUs back in one click — asking again tomorrow is just as easy,
            because machines start on demand. Nothing here needs hoarding.
          </p>
        </div>
        {me.console_paused ? (
          <p className="hero__blocked" role="status">
            The console is paused right now — no workspace can start. Work
            already running is untouched.
          </p>
        ) : (
          <Link className="btn btn--primary btn--hero" to="/new">
            <IconGpus size={16} />
            Get GPUs
          </Link>
        )}
      </section>

      {/* One line about your own week, never blocking the page: hidden while
          it loads and when it cannot be read. Information, not a quota — the
          account page says so at length. */}
      {usage.data && usage.data.gpu_hours > 0 ? (
        <p className="hero__usage">
          You held {formatGpuHours(usage.data.gpu_hours)} GPU-hours this week
          {usage.data.cost.gpu_eur !== null
            ? ` — about ${new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(usage.data.cost.gpu_eur)}`
            : ""}
          . <Link to="/account#account-usage">See your usage</Link>.
        </p>
      ) : null}

      {workspaces.isPending ? (
        <LoadingBlock label="Looking for your workspaces…" />
      ) : workspaces.isError ? (
        <ErrorBlock
          error={workspaces.error}
          onRetry={() => void workspaces.refetch()}
        />
      ) : (
        mine.map((workspace) => (
          <WorkspaceCard
            key={workspace.id}
            workspace={workspace}
            capacity={capacity.data}
          />
        ))
      )}

      {/* A queue is a fact about the fleet, not about you, so it sits between
          your workspaces and the machines: a researcher reads where they stand
          before what is running. */}
      {capacity.data ? (
        <WaitingSection
          rows={capacity.data.queue ?? []}
          unit="GPU"
          you={me.username}
        />
      ) : null}

      {capacity.isPending ? (
        <LoadingBlock label="Measuring what is available…" />
      ) : capacity.isError ? (
        <ErrorBlock error={capacity.error} onRetry={() => void capacity.refetch()} />
      ) : (
        <>
          <CapacitySection capacity={capacity.data} />
          {needsNewMachine(capacity.data) ? (
            <StartMachineCard capacity={capacity.data} />
          ) : null}
        </>
      )}
    </>
  );
}
